import Cookies from "js-cookie";
import { persistUserSession, updateAuthTokenFromResponse } from "./authSession";
import { appStorage } from "./appStorage";
import {
  APP_SETUP_STATIC_INVITE_CODE,
  APP_SETUP_DEFAULT_PLAN_NAME,
  APP_SETUP_DEFAULT_BILLING_CYCLE,
} from "../config/appSetupConfig";

const getApiBase = () => process.env.REACT_APP_API_BASE_URL;

const parseUserFromResponse = (data) => {
  const row = Array.isArray(data?.data) ? data.data[0] : data?.data;
  return row?.user || row || data?.user || null;
};

const getTodayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Detect ios/android for payment-submit (required by mobile IAP endpoint). */
export const detectPaymentPlatform = () => {
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "android";
};

const normalizeEmail = (email) => String(email || "").trim().toLowerCase();

const persistSignupSession = (data, formData = null) => {
  const row = Array.isArray(data?.data) ? data.data[0] : data?.data;
  const user = parseUserFromResponse(data);
  updateAuthTokenFromResponse(data, user, row);
  if (user) {
    persistUserSession(user);
  }
  if (formData) {
    persistAppSetupSignupContext(formData, data);
  }
  return user;
};

/** Keep signup form values in storage even when API user object omits email/phone. */
export const persistAppSetupSignupContext = (formData, apiData = {}) => {
  const user = parseUserFromResponse(apiData);
  const email = normalizeEmail(formData?.email || user?.email || appStorage.getItem("email"));
  const phoneNumber = formData?.phoneNumber || user?.phone_number || appStorage.getItem("phone_number") || "";
  const countryCode = formData?.countryCode || user?.country_code || appStorage.getItem("country_code") || "";
  const firstName = formData?.firstName || user?.first_name || "";
  const lastName = formData?.lastName || user?.last_name || "";

  if (email) {
    appStorage.setItem("email", email);
    appStorage.setItem("user_email", email);
  }
  if (phoneNumber) {
    appStorage.setItem("phone_number", phoneNumber);
    appStorage.setItem("user_phone", phoneNumber);
  }
  if (countryCode) {
    appStorage.setItem("country_code", countryCode);
  }
  if (firstName || lastName) {
    appStorage.setItem("user_name", [firstName, lastName].filter(Boolean).join(" ").trim());
  }
  if (user?.user_id) {
    appStorage.setItem("user_id", String(user.user_id));
  }
};

/** Persist all payment-related session keys after Basic plan activation. */
export const persistAppSetupPaymentSession = (data = {}) => {
  const user = parseUserFromResponse(data);
  const row = Array.isArray(data?.data) ? data.data[0] : data?.data;

  const planStatus =
    user?.plan_status ??
    row?.plan_status ??
    data?.plan_status ??
    data?.payment_plan_status ??
    "1";

  appStorage.setItem("payment_status", "1");
  appStorage.setItem("plan_status", String(planStatus));
  appStorage.setItem("plan_type", user?.plan_type || APP_SETUP_DEFAULT_PLAN_NAME);
  appStorage.setItem("choose_plan_required", "0");
  appStorage.setItem("upgrade_plan_required", "0");

  if (user) {
    persistUserSession({
      ...user,
      payment_status: 1,
      plan_status: planStatus,
      plan_type: user.plan_type || APP_SETUP_DEFAULT_PLAN_NAME,
      choose_plan_required: 0,
      upgrade_plan_required: 0,
    });
  }

  // Re-apply signup context if payment response overwrote email/phone
  const storedEmail = appStorage.getItem("email");
  const storedPhone = appStorage.getItem("phone_number");
  const storedCode = appStorage.getItem("country_code");
  if (storedEmail) {
    appStorage.setItem("email", storedEmail);
    appStorage.setItem("user_email", storedEmail);
  }
  if (storedPhone) {
    appStorage.setItem("phone_number", storedPhone);
    appStorage.setItem("user_phone", storedPhone);
  }
  if (storedCode) appStorage.setItem("country_code", storedCode);
};

export const getDeviceName = () => {
  const userAgent = navigator.userAgent || "";
  const platform = navigator.userAgentData?.platform || navigator.platform || "Unknown device";
  if (/Android/i.test(userAgent)) return "Android";
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "iPhone/iPad";
  if (/Windows/i.test(userAgent)) return "Windows PC";
  if (/Macintosh|Mac OS X/i.test(userAgent)) return "Mac";
  if (/Linux/i.test(userAgent)) return "Linux";
  return platform;
};

export const getClientIpAddress = async () => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) return "";
    const data = await response.json();
    return data?.ip || "";
  } catch {
    return "";
  }
};

export const appSetupSignup = async (formData) => {
  const apiBaseUrl = getApiBase();
  if (!apiBaseUrl) throw new Error("Server configuration error. Please contact support.");

  const [deviceName, ipAddress] = await Promise.all([
    Promise.resolve(getDeviceName()),
    getClientIpAddress(),
  ]);

  const payload = new URLSearchParams({
    first_name: formData.firstName,
    last_name: formData.lastName,
    email: formData.email,
    country_code: formData.countryCode,
    phone_number: formData.phoneNumber,
    password: formData.password,
    deviceType: "Website",
    device_name: deviceName,
    ip_address: ipAddress,
    invite: APP_SETUP_STATIC_INVITE_CODE,
  });

  const response = await fetch(`${apiBaseUrl}/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: payload.toString(),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || "Signup failed");
  }

  appStorage.setItem("email", normalizeEmail(formData.email));
  appStorage.setItem("user_email", normalizeEmail(formData.email));
  appStorage.setItem("phone_number", formData.phoneNumber);
  appStorage.setItem("user_phone", formData.phoneNumber);
  appStorage.setItem("country_code", formData.countryCode);
  appStorage.setItem("user_name", [formData.firstName, formData.lastName].filter(Boolean).join(" ").trim());
  appStorage.setItem("email_verified", "0");
  appStorage.setItem("verification_type", "2");

  persistSignupSession(data, formData);

  if (!Cookies.get("auth_token")) {
    await obtainSessionAfterSignup(formData);
  }

  return data;
};

/** If signup did not return a token, log in once so payment-submit can run. */
export const obtainSessionAfterSignup = async (formData) => {
  if (Cookies.get("auth_token")) return;

  const apiBaseUrl = getApiBase();
  const [deviceName, ipAddress] = await Promise.all([
    Promise.resolve(getDeviceName()),
    getClientIpAddress(),
  ]);

  const payload = new URLSearchParams({
    email: formData.email,
    password: formData.password,
    deviceType: "Website",
    device_name: deviceName,
    ip_address: ipAddress,
  });

  const response = await fetch(`${apiBaseUrl}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: payload.toString(),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === "0" || data.status === 0) {
    throw new Error(data.message || "Unable to start session after signup.");
  }

  const status = data.status ?? data.Status;
  persistSignupSession(data, formData);
  if (status === "2" || status === 2) {
    appStorage.setItem("verification_type", "2");
    appStorage.setItem("email_verified", "0");
  } else if (status === "3" || status === 3) {
    appStorage.setItem("verification_type", "3");
    appStorage.setItem("sms_verified", "0");
  }

  if (!Cookies.get("auth_token")) {
    throw new Error("Unable to start session after signup.");
  }
};

/**
 * Assign Basic plan via POST /payment-submit immediately after signup.
 * Must run before email OTP so the user is on the Basic plan from the start.
 */
export const submitBasicPlanPayment = async (platform = detectPaymentPlatform()) => {
  const apiBaseUrl = getApiBase();
  const token = Cookies.get("auth_token");
  if (!token) {
    throw new Error("Unable to activate Basic plan. Please try signing up again.");
  }

  const payload = new URLSearchParams({
    platform,
    plan_name: APP_SETUP_DEFAULT_PLAN_NAME,
    billing_cycle: APP_SETUP_DEFAULT_BILLING_CYCLE,
    date: getTodayISO(),
    amount: "0",
  });

  const response = await fetch(`${apiBaseUrl}/payment-submit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Bearer ${token}`,
    },
    body: payload.toString(),
  });

  const data = await response.json().catch(() => ({}));
  const status = data.status ?? data.Status;
  const message = data.message ?? data.Message;

  const success =
    status === "1" ||
    status === 1 ||
    response.ok && (status == null || message?.toLowerCase?.().includes("success"));

  if (!success) {
    throw new Error(message || "Failed to activate Basic plan");
  }

  const user = parseUserFromResponse(data);
  if (user) {
    updateAuthTokenFromResponse(data, user);
    persistUserSession(user);
  }

  persistAppSetupPaymentSession(data);

  return data;
};

export const resendEmailOtp = async (email) => {
  const apiBaseUrl = getApiBase();
  const response = await fetch(`${apiBaseUrl}/resend-verify-email-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ email }).toString(),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || "Failed to resend email OTP");
  }
  return data;
};

export const verifyEmailOtp = async (email, otp) => {
  const apiBaseUrl = getApiBase();
  const response = await fetch(`${apiBaseUrl}/verify-email`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ email, otp }).toString(),
  });

  const data = await response.json();
  const status = data.status;
  const message = data.message;
  const user = parseUserFromResponse(data);

  if (status === "0" || status === 0) {
    throw new Error(message || "Invalid email OTP");
  }

  updateAuthTokenFromResponse(data, user);
  persistUserSession(user);

  // Preserve email if API user object does not include it
  const storedEmail = appStorage.getItem("email");
  if (storedEmail) {
    appStorage.setItem("email", storedEmail);
    appStorage.setItem("user_email", storedEmail);
  }

  if (Number(user?.payment_status) === 1) {
    persistAppSetupPaymentSession(data);
  } else {
    appStorage.setItem("choose_plan_required", user?.choose_plan_required?.toString() ?? "");
    appStorage.setItem("upgrade_plan_required", user?.upgrade_plan_required?.toString() ?? "");
  }

  const needsMobileOtp =
    (status === "1" || status === 1) &&
    (user?.sms_verified === 0 || user?.sms_verified === "0") &&
    Boolean(user?.phone_number);

  if (needsMobileOtp) {
    appStorage.setItem("sms_verified", "0");
    appStorage.setItem("verification_type", "3");
  }

  return { status, message, user, needsMobileOtp };
};

export const resendPhoneOtp = async () => {
  const apiBaseUrl = getApiBase();
  const phone = appStorage.getItem("phone_number");
  const code = appStorage.getItem("country_code");
  if (!phone || !code) throw new Error("Phone details not found.");

  const response = await fetch(`${apiBaseUrl}/resend-phone-otp`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      country_code: code,
      phone_number: phone,
      send_via: "sms",
    }).toString(),
  });

  const data = await response.json().catch(() => ({}));
  const status = data.status ?? data.Status;

  if (status === "5" || status === 5) {
    throw new Error(data.message || "Unable to send SMS verification.");
  }

  const ok =
    response.ok &&
    (status === "1" || status === 1 || status === "3" || status === 3 || status == null);

  if (!ok) throw new Error(data.message || "Failed to resend SMS code");
  return data;
};

export const verifyPhoneOtp = async (otp) => {
  const apiBaseUrl = getApiBase();
  const phone = appStorage.getItem("phone_number");
  const code = appStorage.getItem("country_code");
  if (!phone || !code) throw new Error("Phone details not found.");

  const response = await fetch(`${apiBaseUrl}/verify-phone`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      country_code: code,
      phone_number: phone,
      otp,
    }).toString(),
  });

  const data = await response.json();
  const status = data.status ?? data.Status;
  const message = data.message ?? data.Message;
  const user = parseUserFromResponse(data);

  if (status === "0" || status === 0) {
    throw new Error(message || "Invalid mobile OTP");
  }

  const success =
    status === "1" || status === 1 || status === "3" || status === 3;

  if (!success) {
    throw new Error(message || "Invalid mobile OTP");
  }

  updateAuthTokenFromResponse(data, user);
  persistUserSession(user);
  appStorage.setItem("sms_verified", user?.sms_verified?.toString() ?? "1");

  return { status, message, user };
};

const toDDMMYYYY = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const iso = text.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${String(iso[3]).padStart(2, "0")}/${String(iso[2]).padStart(2, "0")}/${iso[1]}`;
  const slash = text.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (slash) return `${String(slash[1]).padStart(2, "0")}/${String(slash[2]).padStart(2, "0")}/${slash[3]}`;
  return text;
};

const PERSONAL_KEYS = [
  "first_name", "last_name", "full_name", "personal_email_id", "work_email_id", "secondary_email",
  "marital_status", "date_of_birth", "date_of_anniversary", "birth_city_country", "current_city_region_country",
  "country_code_personal", "personal_mobile", "basic_pincode_home",
  "country_code_home", "secondary_mobile", "work_mobile_country_code", "work_mobile",
  "country_code_home_fixed", "home_fixed", "country_code_work_fixed", "work_fixed",
];

export const submitPersonalInfo = async (formData) => {
  const apiBaseUrl = getApiBase();
  const token = Cookies.get("auth_token");
  if (!token) throw new Error("Session expired. Please start again.");

  const payload = new URLSearchParams();
  PERSONAL_KEYS.forEach((k) => payload.append(k, formData[k] ?? ""));
  payload.set("date_of_birth", toDDMMYYYY(formData.date_of_birth));
  payload.set("date_of_anniversary", toDDMMYYYY(formData.date_of_anniversary));

  const headers = {
    "Content-Type": "application/x-www-form-urlencoded",
    Authorization: `Bearer ${token}`,
  };

  let response = await fetch(`${apiBaseUrl}/insert_personal_info`, {
    method: "POST",
    headers,
    body: payload.toString(),
  });

  let data = await response.json();
  if (data.status !== "1") {
    response = await fetch(`${apiBaseUrl}/update_personal_info`, {
      method: "POST",
      headers,
      body: payload.toString(),
    });
    data = await response.json();
    if (data.status !== "1") {
      throw new Error(data.message || "Failed to save personal info");
    }
  }

  updateAuthTokenFromResponse(data);
  return data;
};

const convertMonthValue = (v) => {
  if (!v) return "";
  if (v.includes("-")) {
    const [y, m] = v.split("-");
    return `${String(m).padStart(2, "0")}/${y}`;
  }
  if (v.includes("/")) {
    const [m, y] = v.split("/");
    return `${String(m).padStart(2, "0")}/${y}`;
  }
  return v;
};

export const submitEducation = async (educationData, isCurrentStudying) => {
  const apiBaseUrl = getApiBase();
  const token = Cookies.get("auth_token");
  if (!token) throw new Error("Session expired. Please start again.");

  const payload = new URLSearchParams();
  payload.set("edu_institution_name", educationData.edu_institution_name || "");
  payload.set("edu_pin_code", educationData.edu_pin_code || educationData.edu_address_pincode || "");
  payload.set("edu_address_pincode", educationData.edu_address_pincode || educationData.edu_pin_code || "");
  payload.set("edu_city_region_country", educationData.edu_city_region_country || "");
  payload.set("edu_address_city", educationData.edu_address_city || "");
  payload.set("edu_address_region", educationData.edu_address_region || "");
  payload.set("edu_address_country", educationData.edu_address_country || "");
  payload.set("course_name", educationData.course_name || "");
  payload.set("edu_start_date", convertMonthValue(educationData.edu_start_date));
  payload.set(
    "edu_end_date",
    isCurrentStudying ? "" : convertMonthValue(educationData.edu_end_date)
  );
  payload.set("education_current_studying", educationData.education_current_studying || "0");

  const response = await fetch(`${apiBaseUrl}/insert_education`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Bearer ${token}`,
    },
    body: payload.toString(),
  });

  const data = await response.json();
  if (data.status !== "1") {
    throw new Error(data.message || "Failed to save education");
  }
  return data;
};

/** POST /invite/sender — send app store invite for chosen platform (android | ios). */
export const registerWaitlistPlatform = async (email, platform) => {
  const apiBaseUrl = getApiBase();
  if (!apiBaseUrl || apiBaseUrl === "undefined") {
    throw new Error("API base URL is not configured.");
  }

  const token = Cookies.get("auth_token");
  if (!token) {
    throw new Error("Session expired. Please sign up again.");
  }

  const drive = String(platform || "").trim().toLowerCase();
  if (drive !== "android" && drive !== "ios") {
    throw new Error("Please select Android or iOS.");
  }

  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    throw new Error("Email is required.");
  }

  const payload = new URLSearchParams({
    email: normalizedEmail,
    drive,
  });

  const response = await fetch(`${apiBaseUrl}/invite/sender`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: `Bearer ${token}`,
    },
    body: payload.toString(),
  });

  const data = await response.json().catch(() => ({}));
  if (String(data?.status) !== "1") {
    throw new Error(data?.message || "Unable to send app invite.");
  }

  updateAuthTokenFromResponse(data);
  return data;
};
